import type { ReactElement } from 'react'
import type { TFunction } from 'i18next'
import { Column } from 'primereact/column'
import type { LocalizedText } from '../../../lib/localizedText'
import Badge from '../../../components/ui/Badge'
import Button from '../../../components/ui/Button'
import type { TemplateParameter } from '../types'

interface ColumnOpts {
  t: TFunction
  /** İki dilli alanlar tek kolondur; değer seçili dilden gelir (`lib/localizedText`). */
  localized: LocalizedText
  onEdit: (row: TemplateParameter) => void
  /** Tekil kayıt çekilirken düzenle butonları kilitlenir — çift tık engeli. */
  busy?: boolean
  /**
   * Salt okunur kip (126 — Kısıtlı Yetkili asistan): kalem yerine <b>Görüntüle</b> (göz) çizilir.
   * Açıklama ve statik içerik yalnız pop-up'ta göründüğü için `onEdit` aynı tekil yüklemeyle pop-up'ı
   * salt okunur açar.
   */
  readOnly?: boolean
}

/**
 * Şablon Parametresi tablosu sütunları (059 US3).
 *
 * **Parametre kodu kısaltılmaz** (spec US3 AS-4): kullanıcı kodu şablona kopyalayacaktır; `<code>`
 * ile monospace gösterilir ve `user-select` engellenmez.
 *
 * **Sıralama sunucu-taraflıdır** (`useTableSort`): veri sütunlarının tamamı `sortable`, aksiyon
 * sütunu değildir. Sıralama alanı adı sunucudaki beyaz listeyle birebir aynı olmalıdır
 * (`templateParamGroupName`, `nameTr`, `parameterCode`, `isStaticName`, `recordStatus`); Grup ve
 * "Statik mi?" sütunlarında sunucu **gösterilen dile göre çözülmüş adı** sıralar, kimliği değil.
 * Durum sütunu etiket gösterir ama sayısal `recordStatus` üzerinden sıralanır → Aktif/Pasif kararlı
 * biçimde kümelenir. Boş değerler hücrede **boş** bırakılır.
 *
 * <p><b>Satırda yalnız DÜZENLEME vardır</b> (2026-08-04 kararı): **silme yoktur** ve **durum anahtarı
 * yoktur** — parametreler şablon metnindeki yer tutucuların kataloğudur; bir kodun karşılığı gönderim
 * tarafında kodla çözülür, dolayısıyla kaydın varlığı ve kullanılabilirliği ekrandan yönetilecek bir
 * şey değildir. Durum sütunu **gösterim** olarak kalır (pasif parametre şablon panelinde çıkmaz);
 * değiştirilmesi veri tabanı tarafında yapılır.
 */
export function templateParameterColumns({
  t,
  localized,
  onEdit,
  busy,
  readOnly = false,
}: ColumnOpts): ReactElement[] {
  const openLabel = readOnly
    ? t('admin.templateParameters.actions.view')
    : t('admin.templateParameters.actions.edit')
  return [
    <Column
      key="templateParamGroupName"
      field="templateParamGroupName"
      header={t('admin.templateParameters.columns.group')}
      sortable
      body={(row: TemplateParameter) => row.templateParamGroupName ?? ''}
    />,
    <Column
      key="name"
      // Sıralama SUNUCUDA (`lazy` tablo): arka uç beyaz listesi artık dile duyarlı, yani İngilizce
      // arayüzde NAME_EN üzerinden sıralar. İstemciye `sortFunction` koymak yalnız açık sayfayı
      // yeniden dizer ve sunucu sıralamasını bozardı.
      field="nameTr"
      header={t('admin.templateParameters.columns.name')}
      sortable
      body={(row: TemplateParameter) => localized(row.nameTr, row.nameEn)}
    />,
    <Column
      key="parameterCode"
      field="parameterCode"
      header={t('admin.templateParameters.columns.code')}
      sortable
      body={(row: TemplateParameter) => <code className="admin-param-code">{row.parameterCode}</code>}
    />,
    <Column
      key="isStaticName"
      field="isStaticName"
      header={t('admin.templateParameters.columns.isStatic')}
      style={{ width: '8rem' }}
      sortable
      body={(row: TemplateParameter) => row.isStaticName ?? ''}
    />,
    <Column
      key="status"
      header={t('admin.templateParameters.columns.status')}
      style={{ width: '8rem' }}
      // Hücre etiket (Badge) basar → `field` yok; sıralama alanı açıkça verilir.
      sortable
      sortField="recordStatus"
      body={(row: TemplateParameter) => {
        const active = row.recordStatus === 1
        return (
          <Badge tone={active ? 'green' : 'neutral'}>
            {active
              ? t('admin.templateParameters.status.active')
              : t('admin.templateParameters.status.passive')}
          </Badge>
        )
      }}
    />,
    <Column
      key="actions"
      header={t('admin.templateParameters.columns.actions')}
      style={{ width: '5rem' }}
      body={(row: TemplateParameter) => (
        <div className="admin-row-actions">
          <Button
            icon={readOnly ? 'pi pi-eye' : 'pi pi-pencil'}
            rounded
            text
            aria-label={openLabel}
            tooltip={openLabel}
            tooltipOptions={{ position: 'bottom' }}
            disabled={busy}
            onClick={() => onEdit(row)}
          />
        </div>
      )}
    />,
  ]
}
